(() => {
  'use strict';

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const ECONT_BASE = 'https://ee.econt.com/services';
  const CITY_CATALOG_URL = document.currentScript?.dataset.cityCatalogUrl;
  const BOX_NOW_LOCATIONS = 'https://locationapi-production.boxnow.bg/v1/apms_bg-BG.json';

  const METHOD_DEFINITIONS = Object.freeze({
    OFFICE: {
      label: 'До Офис',
      courierName: 'Еконт',
      ratePatterns: [/до\s+офис\s+с\s+еконт/i, /econt.*office/i],
    },
    BOX: {
      label: 'До Автомат',
      courierName: 'BOX NOW',
      ratePatterns: [/box\s*now.*автомат/i, /box\s*now/i],
    },
    ADDRESS: {
      label: 'До Адрес',
      courierName: 'Еконт',
      ratePatterns: [/до\s+адрес\s+с\s+еконт/i, /econt.*address/i],
    },
  });

  const cache = {
    cities: null,
    offices: null,
    boxNow: null,
  };

  const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('bg-BG')
    .replace(/\s+/g, ' ')
    .trim();

  const uniqueMessages = (messages) => [...new Set(messages.map((value) => String(value || '').trim()).filter(Boolean))];

  // Search address words independently: "Витоша 150" must match "бул. Витоша №150".
  // Keep this separate from normalize(), which is also used for validation statuses.
  function searchWords(value) {
    return normalize(value)
      .replace(/ж\s*\.\s*к\s*\.?/gu, ' жк ')
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .split(/\s+/u)
      .filter((word) => word && !['гр', 'град', 'с', 'село', 'ул', 'улица', 'бул', 'булевард', 'жк', 'номер', 'no'].includes(word));
  }

  function oneSearchTypo(left, right) {
    // One insertion, deletion, substitution, or adjacent letter swap in a word.
    if (left.length < 4 || right.length < 4 || /\d/u.test(left + right) || Math.abs(left.length - right.length) > 1) return false;
    let index = 0;
    while (index < Math.min(left.length, right.length) && left[index] === right[index]) index += 1;
    if (left.length === right.length) {
      return left.slice(index + 1) === right.slice(index + 1)
        || (left[index] === right[index + 1] && left[index + 1] === right[index]
          && left.slice(index + 2) === right.slice(index + 2));
    }
    return left.length > right.length
      ? left.slice(index + 1) === right.slice(index)
      : left.slice(index) === right.slice(index + 1);
  }

  function searchRank(queryWords, fields, preferredFields = []) {
    const words = searchWords(fields.filter(Boolean).join(' '));
    // Match numbers exactly, so address 150 does not also match 1500 or postcode 1150.
    let typoCount = 0;
    for (const term of queryWords) {
      if (words.some((word) => /\d/u.test(term) ? word === term : word.startsWith(term))) continue;
      if (!words.some((word) => oneSearchTypo(term, word))) return 99;
      typoCount += 1;
      if (typoCount > 2) return 99;
    }
    // Every query word must match; typo matches always follow exact/prefix matches.
    if (typoCount) return 10 + typoCount;
    const phrase = queryWords.join(' ');
    const preferred = preferredFields.filter(Boolean).map((value) => searchWords(value).join(' '));
    if (preferred.some((value) => value === phrase)) return 0;
    if (preferred.some((value) => value.startsWith(phrase))) return 1;
    return words.join(' ').includes(phrase) ? 2 : 3;
  }

  function collectEcontMessages(value, messages = []) {
    if (!value || typeof value !== 'object') return messages;
    if (typeof value.message === 'string' && value.message.trim()) messages.push(value.message.trim());
    if (Array.isArray(value.innerErrors)) value.innerErrors.forEach((entry) => collectEcontMessages(entry, messages));
    return messages;
  }

  function econtErrorMessage(value, fallback = 'Еконт не успя да потвърди адреса.') {
    const messages = uniqueMessages(collectEcontMessages(value));
    const useful = messages.filter((message) => message.length > 1);
    return useful.join(' ') || fallback;
  }

  async function postJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.type) throw new Error(econtErrorMessage(payload, `Грешка от услугата за доставка (${response.status}).`));
    return payload;
  }

  function shippingQuery(destination = {}) {
    const params = new URLSearchParams();
    params.set('shipping_address[country]', 'Bulgaria');
    params.set('shipping_address[country_code]', 'BG');
    params.set('shipping_address[city]', destination.city || 'София');
    params.set('shipping_address[zip]', destination.postalCode || destination.zip || '1000');
    params.set('shipping_address[address1]', destination.address1 || 'бул. Витоша 1');
    return params.toString();
  }

  async function getShopifyRates(destination = {}) {
    const query = shippingQuery(destination);
    const prepareUrl = `${ROOT}cart/prepare_shipping_rates.json?${query}`;
    const resultUrl = `${ROOT}cart/async_shipping_rates.json?${query}`;
    const prepared = await fetch(prepareUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!prepared.ok) throw new Error('Shopify не успя да изчисли цените за доставка.');

    for (let attempt = 0; attempt < 12; attempt += 1) {
      if (attempt) await new Promise((resolve) => setTimeout(resolve, 300));
      const response = await fetch(resultUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Shopify не успя да върне цените за доставка.');
      const payload = await response.json().catch(() => null);
      if (payload?.shipping_rates) {
        return payload.shipping_rates.map((rate) => ({
          ...rate,
          name: rate.name || rate.presentment_name || rate.code || '',
          code: rate.code || rate.name || rate.presentment_name || '',
          amount: Number(rate.price || 0),
          currencyCode: rate.currency || window.Shopify?.currency?.active || 'EUR',
        }));
      }
    }
    throw new Error('Цените за доставка се изчисляват по-дълго от очакваното. Опитайте отново.');
  }

  function findRateForMethod(method, rates = []) {
    const definition = METHOD_DEFINITIONS[method];
    if (!definition) return null;
    return rates.find((rate) => {
      const searchable = [rate.name, rate.presentment_name, rate.code].filter(Boolean).join(' ');
      return definition.ratePatterns.some((pattern) => pattern.test(searchable));
    }) || null;
  }

  async function loadCities() {
    if (!cache.cities) {
      const live = postJson(
        `${ECONT_BASE}/Nomenclatures/NomenclaturesService.getCities.json`,
        { countryCode: 'BGR' },
      ).then((payload) => Array.isArray(payload.cities) ? payload.cities : []);
      const catalog = CITY_CATALOG_URL
        ? fetch(CITY_CATALOG_URL, { credentials: 'omit' }).then(async (response) => {
          if (!response.ok) throw new Error(`City catalog: HTTP ${response.status}`);
          const payload = await response.json();
          return Array.isArray(payload.cities) ? payload.cities : [];
        })
        : Promise.resolve([]);
      cache.cities = Promise.allSettled([catalog, live]).then((results) => {
        const byId = new Map();
        for (const result of results) {
          if (result.status !== 'fulfilled') continue;
          for (const city of result.value) {
            if (!city?.id || !city.name || !city.postCode) continue;
            byId.set(String(city.id), { ...byId.get(String(city.id)), ...city });
          }
        }
        if (!byId.size) throw new Error('Не успяхме да заредим населените места. Опитайте отново.');
        return [...byId.values()];
      }).catch((error) => {
        cache.cities = null;
        throw error;
      });
    }
    return cache.cities;
  }

  async function searchCities(query, limit = 12) {
    const terms = searchWords(query);
    if (terms.join('').length < 2) return [];
    const cities = await loadCities();
    return cities
      .map((city) => {
        const rank = searchRank(terms, [city.name, city.nameEn, city.postCode, city.regionName], [city.name, city.nameEn]);
        return { city, rank };
      })
      .filter((entry) => entry.rank < 99)
      .sort((left, right) => left.rank - right.rank || String(left.city.name).localeCompare(String(right.city.name), 'bg'))
      .slice(0, limit)
      .map((entry) => entry.city);
  }

  async function loadEcontOffices() {
    if (!cache.offices) {
      cache.offices = postJson(
        `${ECONT_BASE}/Nomenclatures/NomenclaturesService.getOffices.json`,
        { countryCode: 'BGR', showCargoReceptions: false, showLC: false },
      ).then((payload) => (Array.isArray(payload.offices) ? payload.offices : [])
        .filter((office) => office?.code && office?.address?.city && office.isMPS !== true))
        .catch((error) => {
          cache.offices = null;
          throw error;
        });
    }
    return cache.offices;
  }

  async function searchEcontOffices(query, limit = 20) {
    const terms = searchWords(query);
    if (terms.join('').length < 2) return [];
    const offices = await loadEcontOffices();
    return offices
      .map((office) => {
        const city = office.address?.city?.name || '';
        const address = office.address || {};
        const rank = searchRank(terms, [office.code, office.name, office.nameEn, city, address.city?.nameEn,
          address.city?.postCode, address.fullAddress, address.street, address.num, address.quarter, address.other],
        [office.code, office.name, `${city} ${office.name}`]);
        return { office, rank };
      })
      .filter((entry) => entry.rank < 99)
      .sort((left, right) => left.rank - right.rank || String(left.office.name).localeCompare(String(right.office.name), 'bg'))
      .slice(0, limit)
      .map((entry) => ({
        provider: 'econt',
        id: String(entry.office.code),
        name: entry.office.name,
        address1: String(entry.office.address?.fullAddress || '').trim(),
        city: entry.office.address?.city?.name || '',
        postalCode: entry.office.address?.city?.postCode || '',
        note: entry.office.isAPS ? 'Еконтомат' : '',
        raw: entry.office,
      }));
  }

  async function loadBoxNowLocations() {
    if (!cache.boxNow) {
      cache.boxNow = fetch(BOX_NOW_LOCATIONS, { mode: 'cors', credentials: 'omit', headers: { Accept: 'application/json' } })
        .then(async (response) => {
          if (!response.ok) throw new Error('BOX NOW не успя да върне автоматите.');
          const payload = await response.json();
          const values = Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
          return values.filter((location) => location?.id && location?.type === 'apm' && location?.state !== 'inactive');
        })
        .catch((error) => {
          cache.boxNow = null;
          throw error;
        });
    }
    return cache.boxNow;
  }

  async function searchBoxNowLocations(query, limit = 20) {
    const terms = searchWords(query);
    if (terms.join('').length < 2) return [];
    const locations = await loadBoxNowLocations();
    return locations
      .map((location) => {
        const rank = searchRank(terms, [location.id, location.name, location.title, location.addressLine1,
          location.addressLine2, location.postalCode, location.note],
        [String(location.id), location.name, location.title, location.addressLine2]);
        return { location, rank };
      })
      .filter((entry) => entry.rank < 99)
      .sort((left, right) => left.rank - right.rank || String(left.location.name).localeCompare(String(right.location.name), 'bg'))
      .slice(0, limit)
      .map((entry) => ({
        provider: 'boxnow',
        id: String(entry.location.id),
        name: entry.location.name || entry.location.title,
        address1: entry.location.addressLine1 || entry.location.name || '',
        city: entry.location.addressLine2 || '',
        postalCode: entry.location.postalCode || '',
        note: entry.location.note || '',
        raw: entry.location,
      }));
  }

  function parseDoorAddress(fullAddress, city) {
    const value = String(fullAddress || '').replace(/\s+/g, ' ').trim();
    if (!value) throw new Error('Въведете адрес за доставка.');

    const blockMarker = value.match(/(?:^|[\s,;])(бл\.?|блок)\s*([0-9]+[а-яa-z]?)/i);
    if (blockMarker) {
      const markerIndex = Math.max(0, blockMarker.index || 0);
      const quarter = value.slice(0, markerIndex).replace(/[,;\s]+$/, '').trim() || city?.name || '';
      return {
        city: { country: { code3: 'BGR' }, id: city?.id, name: city?.name, postCode: city?.postCode },
        quarter,
        other: value.slice(markerIndex).trim() || value,
      };
    }

    const numberMatch = value.match(/^(.*?)(?:\s*(?:№|номер|no\.?)\s*|\s+)(\d+[а-яa-z]?(?:\/\d+[а-яa-z]?)?)(?:\s*[,;]\s*|\s+)?(.*)$/i);
    if (!numberMatch || !numberMatch[1]?.trim()) {
      throw new Error('Добавете номер на улица или данни за блок и вход.');
    }
    return {
      city: { country: { code3: 'BGR' }, id: city?.id, name: city?.name, postCode: city?.postCode },
      street: numberMatch[1].trim(),
      num: numberMatch[2].trim(),
      other: numberMatch[3]?.trim() || '',
    };
  }

  function citySupportsDoorDelivery(city) {
    const servingOffices = Array.isArray(city?.servingOffices) ? city.servingOffices : [];
    if (!servingOffices.length) return true;
    return servingOffices.some((entry) => /^to_door_(courier|post)$/i.test(String(entry?.servingType || '')));
  }

  function tomorrowIsoDate() {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  function shipmentWeightKg(cart) {
    const grams = Number(cart?.total_weight || 0);
    return Math.max(0.1, Math.round((grams / 1000) * 100) / 100 || 0.1);
  }

  async function validateEcontAddress({ city, address1, customer = {}, cart = null }) {
    if (!city?.id || !city?.name) throw new Error('Изберете населено място от предложенията на Еконт.');
    if (!citySupportsDoorDelivery(city)) throw new Error('Еконт не предлага доставка до адрес в това населено място.');

    const requestAddress = parseDoorAddress(address1, city);
    const validation = await postJson(
      `${ECONT_BASE}/Nomenclatures/AddressService.validateAddress.json`,
      { address: requestAddress },
    );
    const status = normalize(validation?.validationStatus);
    if (!['normal', 'processed'].includes(status)) throw new Error('Еконт не разпознава този адрес. Проверете улицата, номера или блока.');

    const normalizedAddress = validation?.address?.fullAddress || address1;
    const serviceTimes = await postJson(
      `${ECONT_BASE}/Nomenclatures/AddressService.addressServiceTimes.json`,
      { city: Number(city.id), address: normalizedAddress, date: tomorrowIsoDate(), shipmentType: 'pack' },
    );
    if (!serviceTimes?.serviceOffice) throw new Error('Еконт не обслужва този адрес в момента.');

    const phone = String(customer.phone || '').trim();
    const receiverName = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim() || 'Клиент';
    if (!phone) throw new Error('Въведете телефон, за да проверим доставката с Еконт.');

    const labelValidation = await postJson(
      `${ECONT_BASE}/Shipments/LabelService.createLabel.json`,
      {
        label: {
          senderClient: { name: 'КРИСТЪЛ ЕНЕРДЖИ ЕООД', phones: [phone] },
          senderAgent: { name: 'Онлайн магазин', phones: [phone] },
          senderAddress: {
            city: { country: { code3: 'BGR' }, name: 'Русе', postCode: '7013' },
            street: 'Рига',
            num: '8',
          },
          receiverClient: { name: receiverName, phones: [phone] },
          receiverAddress: requestAddress,
          packCount: 1,
          shipmentType: 'PACK',
          weight: shipmentWeightKg(cart),
          shipmentDescription: 'Онлайн поръчка',
        },
        mode: 'validate',
      },
    );
    if (!labelValidation?.label) throw new Error('Еконт не потвърди доставката до този адрес.');

    return {
      valid: true,
      normalizedAddress,
      city,
      serviceOffice: serviceTimes.serviceOffice,
      warning: labelValidation.delayedDeliveryWarning || '',
      rawAddress: validation.address || requestAddress,
    };
  }

  function pickupPointShape(location) {
    if (!location) return null;
    return {
      provider: location.provider,
      details: {
        locationOfficeId: location.id,
        name: location.name,
        city: location.city,
        note: location.note || '',
      },
      address: {
        address1: location.address1,
        city: location.city,
        postalCode: location.postalCode,
      },
      raw: location.raw,
    };
  }

  window.CrystalShipping = Object.freeze({
    methods: METHOD_DEFINITIONS,
    getShopifyRates,
    findRateForMethod,
    searchCities,
    searchEcontOffices,
    searchBoxNowLocations,
    validateEcontAddress,
    pickupPointShape,
    econtErrorMessage,
  });
})();
